import Button from '@atoms/Button/Button';
import Image from '@atoms/Image/Image';
import PriceDisplayText from '@atoms/PriceDisplayText/PriceDisplayText';
import ShadowContainerHorizontal, {
  IShadowContainerHorizontalRef,
} from '@atoms/ShadowContainerHorizontal/ShadowContainerHorizontal';
import { IImage } from '@models/IImage';
import { ILink } from '@models/ILink';
import { IPricingData } from '@models/IPricingData';
import useComponentsHeightsStore from '@state/ComponentsHeightsStore';
import updateDataLayer from '@utils/helpers/gtm';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import { usePageScrollProgress } from '@utils/hooks/usePageScrollProgress';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props {
  readonly buyNowLink?: ILink;
  readonly fromPriceLabel?: string;
  readonly icon?: IImage;
  readonly pricingData?: IPricingData;
  readonly shippingLabel?: string;
  readonly shouldHavePadding?: boolean;
  readonly subTitle?: string;
  readonly tabbedNavigation: ILink[];
  readonly thumbnail?: IImage;
  readonly title?: string;
  readonly vatLabel?: string;
}

const TabbedProductNavigation = ({
  buyNowLink,
  fromPriceLabel,
  icon,
  pricingData,
  shippingLabel,
  shouldHavePadding,
  subTitle,
  tabbedNavigation,
  thumbnail,
  title,
  vatLabel,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const scrollProgress = usePageScrollProgress();
  const tabbedNavRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<IShadowContainerHorizontalRef>(null);
  const [animationComplete, setAnimationComplete] = useState(false);
  const {
    setTabbedProductNavigationTopHeight,
    setTabbedProductNavigationBottomHeight,
  } = useComponentsHeightsStore((state) => ({
    setTabbedProductNavigationTopHeight:
      state.setTabbedProductNavigationTopHeight,
    setTabbedProductNavigationBottomHeight:
      state.setTabbedProductNavigationBottomHeight,
  }));
  const [topTabbedNavRefCallback, { clientHeight: topTabbedNavHeight }] =
    useResizeObserver();
  const [
    bottomNavDetailsRefCallback,
    { clientHeight: bottomNavDetailsHeight },
  ] = useResizeObserver();
  const [bottomTabbedNavRefCallback, { clientHeight: bottomTabbedNavHeight }] =
    useResizeObserver();

  const combinedTabNavRef = (node: HTMLDivElement | null) => {
    tabbedNavRef.current = node;
    bottomTabbedNavRefCallback(node);
  };

  useEffect(() => {
    setTabbedProductNavigationTopHeight(topTabbedNavHeight);
  }, [setTabbedProductNavigationTopHeight, topTabbedNavHeight]);

  useEffect(() => {
    setTabbedProductNavigationBottomHeight(
      bottomNavDetailsHeight + bottomTabbedNavHeight
    );
  }, [
    setTabbedProductNavigationBottomHeight,
    bottomNavDetailsHeight,
    bottomTabbedNavHeight,
  ]);

  useEffect(() => {
    const activeNavigationElement = tabbedNavRef.current?.querySelector(
      'a[data-nav-active="true"]'
    ) as HTMLElement | null;

    // Need to wait for the animation to complete before scrolling
    if (activeNavigationElement && animationComplete) {
      const containerWidth = containerRef.current!.getOffsetWidth();
      const elementLeft = activeNavigationElement.offsetLeft;
      const elementWidth = activeNavigationElement.offsetWidth;
      const scrollTarget = elementLeft + elementWidth / 2 - containerWidth / 2;
      const clampedOffset = Math.max(0, scrollTarget);

      containerRef.current!.scrollTo(clampedOffset);
    }
  }, [animationComplete]);

  const handleTabbedNavItemClick = (itemName?: string) => {
    updateDataLayer({
      event: 'menuUsed',
      content_type: 'tabbed_pdp_menu',
      main_category: itemName,
    });
  };

  return (
    <>
      {tabbedNavigation.length > 1 && (
        <motion.section
          ref={topTabbedNavRefCallback}
          className={classNames(
            'hidden h-15 overflow-hidden bg-grey-600 lg:flex',
            {
              'mb-24': shouldHavePadding,
            }
          )}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          data-testid="CT-63-Tabbed-Product-Navigation-Desktop-Nav"
        >
          <div className="grid w-full grid-cols-[repeat(auto-fit,_minmax(0,_1fr))] gap-x-1 lg:gap-x-7.5">
            {tabbedNavigation.map(({ name, url, isActive }, index) => (
              <div
                key={name}
                className="relative flex items-center justify-center"
                data-testid={`CT-63-Tabbed-Product-Navigation-Desktop-Nav-Item-${index}`}
              >
                <a
                  href={url}
                  className={classNames(
                    'small-text !font-bold !tracking-[0.3em] text-center text-grey-500 uppercase transition-all duration-100 hover:text-yellow',
                    { 'text-white': isActive }
                  )}
                  onClick={() => {
                    handleTabbedNavItemClick(name);
                  }}
                  data-testid={`CT-63-Tabbed-Product-Navigation-Desktop-Nav-Item-${index}-Link`}
                >
                  {name}
                </a>

                {isActive && (
                  <div className="absolute bottom-0 h-1 w-full bg-grey-450">
                    <span
                      className="block h-full bg-yellow"
                      style={{ width: `${scrollProgress * 100}%` }}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.section>
      )}

      <div
        className={classNames(
          'block lg:hidden',
          shouldHavePadding ? 'mb-7.5' : ''
        )}
      />

      <motion.div
        ref={bottomNavDetailsRefCallback}
        className={classNames(
          {
            'pt-20': tabbedNavigation.length === 0,
          },
          'dark fixed bottom-0 z-10 flex min-h-[76px] w-full flex-row items-center bg-black-100 px-5 py-[15px] lg:bg-black-100 xl:px-7.5 xl:py-6'
        )}
        data-testid="CT-63-Tabbed-Product-Navigation"
      >
        <AnimatePresence>
          {tabbedNavigation.length > 1 && (
            <motion.div
              ref={combinedTabNavRef}
              className="absolute right-0 bottom-0 left-0 bg-grey-600 text-center lg:hidden lg:pr-0"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{
                duration: 0.5,
                ease: [0.74, 0, 0.19, 1.02],
              }}
              data-testid="CT-63-Tabbed-Product-Navigation-Mobile-Nav"
              style={{
                ...(isMobile ? { bottom: bottomNavDetailsHeight } : {}),
              }}
              onAnimationComplete={() => setAnimationComplete(true)}
            >
              <ShadowContainerHorizontal
                ref={containerRef}
                shadowColour="from-black-100"
                className="flex h-15 items-center"
                threshold={0.01}
                hasTabbableNestedItems
              >
                {tabbedNavigation.map(({ name, url, isActive }, index) => (
                  <a
                    key={name}
                    href={url}
                    onClick={() => {
                      handleTabbedNavItemClick(name);
                    }}
                    className={classNames(
                      'small-text !font-bold !tracking-[0.3em] ml-10 text-grey-500 uppercase transition-all duration-100 hover:text-grey-500',
                      { 'text-white': isActive },
                      { 'ml-8': index === 0 },
                      { 'mr-8': index === tabbedNavigation.length - 1 }
                    )}
                    data-nav-active={isActive?.toString()}
                    data-testid={`CT-63-Tabbed-Product-Navigation-Mobile-Nav-Item-${index}`}
                  >
                    {name}
                  </a>
                ))}
              </ShadowContainerHorizontal>

              <div className="h-1 w-full bg-grey-450">
                <span
                  className="block h-full bg-yellow"
                  style={{ width: `${scrollProgress * 100}%` }}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {buyNowLink?.name && (
          <div className="flex w-1/2 flex-col items-start text-white md:flex-row md:items-center xl:w-1/3">
            {thumbnail && (
              <Image
                image={thumbnail}
                imageClasses="md:mr-6 w-20 object-contain hidden md:block"
                sizes="300px"
                dataTestId="CT-63-Tabbed-Product-Navigation-Thumbnail"
              />
            )}

            <div className="md:pr-7.5">
              {icon ? (
                <Image
                  image={icon}
                  imageClasses="w-7.5 mb-2"
                  sizes="300px"
                  dataTestId="CT-63-Tabbed-Product-Navigation-Icon"
                />
              ) : (
                <p
                  className="body-two"
                  data-testid="CT-63-Tabbed-Product-Navigation-Title"
                >
                  {title}
                </p>
              )}

              {subTitle && (
                <p
                  className="body-four hidden md:block"
                  data-testid="CT-63-Tabbed-Product-Navigation-Subtitle"
                >
                  {subTitle}
                </p>
              )}
            </div>

            {pricingData?.formattedCalculatedPrice && (
              <div className="border-white md:mt-0 md:border-l md:pl-7.5">
                <PriceDisplayText
                  pricingData={pricingData}
                  vatLabel={isMobile ? undefined : vatLabel}
                  shippingLabel={isMobile ? undefined : shippingLabel}
                  fromPriceLabel={fromPriceLabel}
                  dataTestId="CT-63-Tabbed-Product-Navigation-Price-Display-Text"
                  containerFontSize={isMobile ? 'body-four' : 'body-two'}
                  containerClassName="text-grey-400 lg:text-white"
                  priceClassName="!font-light"
                  taxesTopMarginClassName="mt-0.5"
                />
              </div>
            )}
          </div>
        )}

        {buyNowLink?.name && buyNowLink.url && (
          <div className="ml-auto flex h-full w-1/2 select-none items-center justify-end xl:w-2/3">
            <div className="flex items-center">
              <Button
                link={buyNowLink}
                linkCallback={() => {
                  updateDataLayer({
                    event: 'menuUsed',
                    content_type: 'sticky_buy_bar',
                    main_category: buyNowLink.name,
                  });
                }}
                dataTestId="CT-63-Tabbed-Product-Navigation-Buy-Now-Link"
                className="text-center lg:ml-5 xl:ml-7.5"
              />
            </div>
          </div>
        )}
      </motion.div>
    </>
  );
};

export default TabbedProductNavigation;
